import type { ReactNode } from 'react';
import {
  Badge,
  Collapse,
  Group,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { ChevronDown, ChevronUp } from 'react-feather';
import { ContentCard } from '../../../components/common/ContentCard';

export type StepCompletionStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

const TITLE_COLOR = '#1A1A2E';

interface PrepStepCardProps {
  /**
   * MED-1092 — leading icon (gray, ~size 20), mirroring the coach Session
   * Agenda card's icon + title header instead of a numbered circle.
   */
  icon: ReactNode;
  title: string;
  /**
   * AC11 — drives the step-completion indicator pill.
   * 'NOT_STARTED' renders gray, 'IN_PROGRESS' amber, 'COMPLETED' medleyGreen.
   */
  status: StepCompletionStatus;
  /** Controlled expand/collapse — page owns state so step toggles can interlock. */
  expanded: boolean;
  onToggleExpanded: () => void;
  /** Optional right-edge content rendered next to the status pill (e.g., Mark as Reviewed). */
  headerActions?: ReactNode;
  children: ReactNode;
}

const STATUS_LABELS: Record<StepCompletionStatus, string> = {
  NOT_STARTED: 'NOT STARTED',
  IN_PROGRESS: 'IN PROGRESS',
  COMPLETED: 'COMPLETED',
};

const STATUS_COLORS: Record<StepCompletionStatus, string> = {
  NOT_STARTED: 'gray',
  IN_PROGRESS: 'warningAmber',
  COMPLETED: 'medleyGreen',
};

/**
 * AC11 — Reusable collapsible step container for the peer-leader prep page.
 * MED-1092 — header is a leading icon + title + status pill (mirroring the coach
 * Session Agenda card), with the chevron toggle on the right. (Previously a
 * numbered circle; dropped because the 1–5 card framing left the visible badges
 * starting at "2".)
 *
 * The page owns the `expanded` state so it can coordinate AC7 (Step 1
 * auto-collapses on COMPLETED) and AC8 (Step 1 expanded by default when
 * the reflection hasn't been submitted).
 */
export function PrepStepCard({
  icon,
  title,
  status,
  expanded,
  onToggleExpanded,
  headerActions,
  children,
}: PrepStepCardProps) {
  return (
    // MED-1092 — built on the shared ContentCard (elevated, radius 16) so the
    // stepped prep cards match the coach prep surfaces. The page owns the
    // expand/collapse state (AC7/AC10 interlocks), so we drive a controlled
    // Collapse rather than ContentCard's self-managed `collapsible`; the
    // clickable header goes in the header slot and contentGap=0 keeps the card
    // tight to its header when collapsed.
    <ContentCard
      variant="elevated"
      radius={16}
      padding="lg"
      contentGap={0}
      fill={false}
      header={
        // The toggle target and `headerActions` (e.g. "Mark reviewed") must be
        // SIBLINGS — a <button> can't contain another <button> (invalid HTML).
        // So the title area and the chevron are separate buttons; headerActions
        // sits between them.
        <Group justify="space-between" wrap="nowrap" align="center">
          <UnstyledButton
            onClick={onToggleExpanded}
            aria-expanded={expanded}
            style={{ flex: 1, minWidth: 0 }}
          >
            <Group gap="sm" wrap="nowrap" align="center">
              {icon}
              <Text fw={600} fz={17} c={TITLE_COLOR}>
                {title}
              </Text>
              {/* MED-1092 — match the Session Prep badge "grail" (the coach
                  agenda's "All Groups" pill): radius xl, 12px/600, +1px nudge,
                  0.6 tracking. Keeps the status color (gray / amber / green). */}
              <Badge
                color={STATUS_COLORS[status]}
                variant={status === 'NOT_STARTED' ? 'light' : 'filled'}
                radius="xl"
                tt="uppercase"
                styles={{
                  root: { letterSpacing: '0.6px' },
                  label: {
                    fontSize: 12,
                    fontWeight: 600,
                    transform: 'translateY(1px)',
                  },
                }}
              >
                {STATUS_LABELS[status]}
              </Badge>
            </Group>
          </UnstyledButton>
          <Group gap="sm" wrap="nowrap" align="center">
            {headerActions}
            <UnstyledButton
              onClick={onToggleExpanded}
              aria-label={expanded ? 'Collapse' : 'Expand'}
              style={{ display: 'inline-flex' }}
            >
              {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </UnstyledButton>
          </Group>
        </Group>
      }
    >
      <Collapse in={expanded}>
        <Stack gap="md" mt="md">
          {children}
        </Stack>
      </Collapse>
    </ContentCard>
  );
}
